import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Send, RefreshCw, ExternalLink, FileText } from "lucide-react";

const STATUS_STYLE = {
  draft: "bg-muted text-forest",
  open: "bg-safety text-white",
  paid: "bg-forest text-white",
  void: "bg-muted text-muted-foreground",
  uncollectible: "bg-destructive text-white",
};

export default function AdminInvoices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [form, setForm] = useState({
    customer_email: "",
    customer_name: "",
    due_days: 30,
    memo: "",
    target_type: "",
    target_id: "",
  });
  const [lines, setLines] = useState([
    { description: "", amount: "", quantity: 1 },
  ]);

  const load = () =>
    api.get(`/invoices${statusFilter !== "all" ? `?status=${statusFilter}` : ""}`)
      .then(({ data }) => setItems(data.items || []))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load invoices"))
      .finally(() => setLoading(false));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const updateLine = (i, field, value) => {
    const next = [...lines];
    next[i] = { ...next[i], [field]: value };
    setLines(next);
  };
  const addLine = () => setLines([...lines, { description: "", amount: "", quantity: 1 }]);
  const removeLine = (i) => {
    if (lines.length === 1) return;
    setLines(lines.filter((_, idx) => idx !== i));
  };

  const total = lines.reduce(
    (sum, l) => sum + (parseFloat(l.amount || 0) * parseInt(l.quantity || 1, 10)),
    0,
  );

  const submit = async (e) => {
    e.preventDefault();
    if (lines.some((l) => !l.description || !l.amount || Number(l.amount) <= 0)) {
      toast.error("Every line needs a description and a positive amount");
      return;
    }
    setSending(true);
    try {
      const payload = {
        customer_email: form.customer_email,
        customer_name: form.customer_name || null,
        due_days: Number(form.due_days),
        memo: form.memo || null,
        target_type: form.target_type || null,
        target_id: form.target_id || null,
        line_items: lines.map((l) => ({
          description: l.description,
          amount: Number(l.amount),
          quantity: parseInt(l.quantity || 1, 10),
        })),
      };
      await api.post("/invoices", payload);
      toast.success(`Invoice sent to ${form.customer_email}`);
      setForm({ customer_email: "", customer_name: "", due_days: 30, memo: "", target_type: "", target_id: "" });
      setLines([{ description: "", amount: "", quantity: 1 }]);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send invoice");
    } finally { setSending(false); }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-10">
      <div className="overline text-safety">Admin</div>
      <div className="mt-2 flex items-start justify-between flex-wrap gap-3">
        <h1 className="font-display font-black text-4xl tracking-tighter text-forest">Invoices</h1>
        <Button variant="ghost" size="sm" onClick={load} className="rounded-none" data-testid="invoices-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>
      <p className="mt-3 text-muted-foreground max-w-xl">
        Send Stripe-hosted invoices to corporate buyers on net terms. The recipient gets an email with a pay-online link — Stripe handles reminders, dunning, and reconciliation.
      </p>

      {/* Create-invoice form */}
      <form onSubmit={submit} className="mt-8 border border-border bg-white p-6 lg:p-8 space-y-5" data-testid="invoice-create-form">
        <div className="flex items-center justify-between border-b border-border pb-4">
          <div className="font-display text-2xl tracking-tight text-forest">New invoice</div>
          <div className="overline text-muted-foreground">GBP · VAT inclusive</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Customer email *">
            <Input required type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="invoice-email" className="rounded-none h-11" />
          </Field>
          <Field label="Customer name">
            <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="invoice-name" className="rounded-none h-11" />
          </Field>
          <Field label="Due (days) *">
            <Input required type="number" min="1" max="180" value={form.due_days} onChange={(e) => setForm({ ...form, due_days: e.target.value })} data-testid="invoice-due" className="rounded-none h-11" />
          </Field>
          <Field label="Ref (e.g. brief_id / job_id)">
            <Input value={form.target_id} onChange={(e) => setForm({ ...form, target_id: e.target.value })} placeholder="brf_… or job_001" data-testid="invoice-target" className="rounded-none h-11" />
          </Field>
        </div>

        <Field label="Memo (visible on the invoice)">
          <Textarea rows={2} value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} data-testid="invoice-memo" className="rounded-none" />
        </Field>

        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="overline text-muted-foreground">Line items</div>
            <Button type="button" size="sm" onClick={addLine} data-testid="invoice-add-line" className="rounded-none bg-forest hover:bg-forest-800 text-white h-9 px-3">
              <Plus className="w-4 h-4 mr-1" /> Add line
            </Button>
          </div>
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-[1fr,120px,80px,auto] gap-2 items-start" data-testid={`invoice-line-${i}`}>
                <Input placeholder="Description" value={l.description} onChange={(e) => updateLine(i, "description", e.target.value)} data-testid={`invoice-line-desc-${i}`} className="rounded-none h-11" />
                <Input placeholder="£ Amount" type="number" step="0.01" min="0.01" value={l.amount} onChange={(e) => updateLine(i, "amount", e.target.value)} data-testid={`invoice-line-amount-${i}`} className="rounded-none h-11" />
                <Input placeholder="Qty" type="number" min="1" value={l.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} data-testid={`invoice-line-qty-${i}`} className="rounded-none h-11" />
                <Button type="button" variant="ghost" onClick={() => removeLine(i)} disabled={lines.length === 1} data-testid={`invoice-line-remove-${i}`} className="rounded-none h-11">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-4 border-t border-border flex items-center justify-between gap-3">
          <div>
            <div className="overline text-muted-foreground">Invoice total</div>
            <div className="font-display text-3xl tabular-nums text-forest">£{total.toFixed(2)}</div>
          </div>
          <Button type="submit" disabled={sending} data-testid="invoice-send-btn" className="rounded-none bg-safety hover:bg-safety-hover text-white h-12 px-8">
            <Send className="w-4 h-4 mr-2" /> {sending ? "Sending…" : "Send invoice"}
          </Button>
        </div>
      </form>

      {/* Table */}
      <section className="mt-10">
        <div className="flex items-center justify-between mb-3">
          <div className="overline text-muted-foreground">Sent invoices</div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="invoices-status-filter" className="rounded-none border border-border h-9 px-3 text-sm">
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="paid">Paid</option>
            <option value="draft">Draft</option>
            <option value="void">Void</option>
            <option value="uncollectible">Uncollectible</option>
          </select>
        </div>

        {loading ? (
          <div className="border border-border bg-white p-12 text-center text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <div className="border border-border bg-white p-12 text-center" data-testid="invoices-empty">
            <FileText className="w-10 h-10 mx-auto text-muted-foreground" />
            <div className="mt-3 text-sm text-muted-foreground">No invoices yet.</div>
          </div>
        ) : (
          <div className="border border-border bg-white">
            <div className="hidden md:grid grid-cols-[1fr,120px,110px,120px,90px] gap-4 p-4 overline text-[10px] text-muted-foreground border-b border-border">
              <div>Customer</div>
              <div className="text-right">Amount</div>
              <div>Status</div>
              <div>Due</div>
              <div className="text-right">Link</div>
            </div>
            {items.map((inv) => (
              <div key={inv.invoice_id} data-testid={`invoice-row-${inv.invoice_id}`} className="grid grid-cols-[1fr,120px,110px,120px,90px] gap-4 p-4 items-center border-t border-border first:border-t-0 hover:bg-sage-light transition-colors">
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{inv.customer_name || inv.customer_email}</div>
                  <div className="text-xs text-muted-foreground truncate">{inv.customer_email}</div>
                  {inv.target_id && <div className="text-[10px] text-safety overline mt-1">{inv.target_type || "ref"}: {inv.target_id}</div>}
                </div>
                <div className="text-right font-display text-lg tabular-nums text-forest">£{Number(inv.amount_due).toFixed(2)}</div>
                <div>
                  <Badge className={`rounded-none overline text-[9px] ${STATUS_STYLE[inv.status] || "bg-muted text-forest"}`}>
                    {inv.status}
                  </Badge>
                </div>
                <div className="text-xs">{inv.due_date ? new Date(inv.due_date).toLocaleDateString("en-GB") : "—"}</div>
                <div className="text-right">
                  {inv.hosted_invoice_url && (
                    <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" data-testid={`invoice-open-${inv.invoice_id}`} className="inline-flex items-center gap-1 text-xs text-safety hover:text-forest overline">
                      Open <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="overline text-muted-foreground mb-2">{label}</div>
      {children}
    </div>
  );
}
